import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import includes from 'lodash/includes';
import { createSelector } from 'reselect';

import { ENTITIES_KEY_FORM_TEMPLATE } from 'common/entities/form';

import { getInputTypeDisplayName } from './index';

import reducerNamespace from '../constants/reducerNamespace';
import {
    OTHER_CATEGORY,
    RESERVED_CATEGORY,
    ADDITIONAL_GUEST_CATEGORY,
    INPUT_TYPE_CATEGORY,
    NON_RESERVED_CATEGORIES,
} from '../constants/templateLibrary';

const TEMPLATE_LIBRARY = 'templateLibrary';

const createTemplatesSelector = (predicate) => (state) => {
    const templates = get(state, `entities.${ENTITIES_KEY_FORM_TEMPLATE}`, {});

    if (!templates) {
        return [];
    }

    return Object.values(templates)
        .map((template) => ({
            ...template,
            inputTypeLabel: template.data.inputType && getInputTypeDisplayName(template.data.inputType)(state),
        }))
        .filter(predicate);
};

/**
 * @param  {Object} state
 * @return {Object[]}
 */
export const getStockTemplates = createTemplatesSelector((template) => template.isStock);

/**
 * @param  {Object} state
 * @return {Object[]}
 */
export const getCustomTemplatesEntities = createTemplatesSelector((template) => !template.isStock);

/**
 * @param  {Object} state
 * @return {string[]}
 */
export const getStockCategories = (state) => {
    const stockTemplates = getStockTemplates(state);

    const templateGroups = groupBy(stockTemplates, (template) => template.category || OTHER_CATEGORY);

    return Object.keys(templateGroups);
};

export const getNonInputTypeCategoryTemplates = createSelector(getStockTemplates, (templates) =>
    templates.filter((template) => template.category !== INPUT_TYPE_CATEGORY),
);

export const getCategoryFilteredTemplates = (valuesToFilter) =>
    createSelector(getStockTemplates, (templates) =>
        templates.filter((template) => includes(valuesToFilter, template.category)),
    );

export const getReservedCategoryTemplates = getCategoryFilteredTemplates([RESERVED_CATEGORY]);

export const getAdditionalReservedCategoryTemplates = getCategoryFilteredTemplates([ADDITIONAL_GUEST_CATEGORY]);

export const getNonReservedCategoryTemplates = getCategoryFilteredTemplates([...NON_RESERVED_CATEGORIES]);

export const getInputTypeCategoryTemplates = getCategoryFilteredTemplates([INPUT_TYPE_CATEGORY]);

const getTemplateLibraryNamespace = (state) => state[reducerNamespace][TEMPLATE_LIBRARY];

/**
 * @param  {Object} state
 * @return {String}
 */
export const getCustomSearchTerm = (state) => getTemplateLibraryNamespace(state).customSearchTerm;

/**
 * @param  {Object} state
 * @return {String}
 */
export const getStockTemplateSearchTerm = (state) => getTemplateLibraryNamespace(state).stockSearchTerm;

/**
 * @param  {Object} state
 * @return {bool}
 */
export const getIsOpen = (state) => getTemplateLibraryNamespace(state).isOpen;

/**
 * @param  {Object} state
 * @return {Array}
 */
export const getCustomTemplatesIds = (state) => getTemplateLibraryNamespace(state).customTemplates;

/**
 * @param  {Object} state
 * @return {bool}
 */
export const getIsLoadingTemplateLibrary = (state) => getTemplateLibraryNamespace(state).isLoadingTemplateLibrary;
/**
 * @param  {Object} state
 * @return {string}
 */
export const getActiveTab = (state) => getTemplateLibraryNamespace(state).activeTab;

/**
 * @param {Object} state
 * @return {number}
 */
export const getTotalPages = (state) => getTemplateLibraryNamespace(state).totalPages;

/**
 * @param {Object} state
 * @return {number}
 */
export const getLastPageLoaded = (state) => getTemplateLibraryNamespace(state).lastPageLoaded;

/**
 * @param {Object} state
 * @return {string}
 */
export const getSelectedTemplateId = (state) => getTemplateLibraryNamespace(state).selectedTemplateId;

/**
 * @param {Object} state
 * @return {bool}
 */
export const getAreAllPagesLoaded = createSelector(
    [getTotalPages, getLastPageLoaded],
    (totalPages, lastPageLoaded) => lastPageLoaded >= totalPages,
);

/**
 * @param {Object} state
 * @return {bool}
 */
export const getIsLoadingNextPage = (state) => getTemplateLibraryNamespace(state).isLoadingNextPage;

export const getCustomTemplates = createSelector(getCustomTemplatesEntities, getCustomTemplatesIds, (templates, ids) =>
    templates.filter((template) => includes(ids, template.id)),
);

export const getMode = (state) => getTemplateLibraryNamespace(state).mode;

export const getIsAdditional = (state) => getTemplateLibraryNamespace(state).isAdditional;

/**
 * @param {Object} state
 * @return {string|null} - returns the node ID of the group where the question would be added to.
 */
export const getNodeGroupId = (state) => getTemplateLibraryNamespace(state).nodeGroupId;

export const getSelectedTemplate = (state) => {
    const selectedTemplateId = getSelectedTemplateId(state);
    return get(state, `entities.${ENTITIES_KEY_FORM_TEMPLATE}[${selectedTemplateId}]`);
};
